/**
 *
 * ResultsSjTableHead
 *
 */

import React, { memo, FunctionComponent } from 'react';
import { MOBILE_BREAKPOINT, TABLET_BREAKPOINT } from '../../../../app/constants';
import SortIndicatorComponent from '../../../../components/SortIndicator/SortIndicator.component';
import { Sort } from '../../../../domain';
import useWindowSize from '../../../../hooks/useWindowSize';

interface IProps {
  changeSort: (field: string) => void;
  sort?: Sort;
  sjPhasesCount: number;
  disabledFields: string[];
}

const ResultsSJTableHead: FunctionComponent<IProps> = ({
  changeSort,
  sort,
  sjPhasesCount,
  disabledFields,
}) => {
  const { width = 0 } = useWindowSize();
  const desktopVisible = width > TABLET_BREAKPOINT;
  const tabletVisible = width <= TABLET_BREAKPOINT && width > MOBILE_BREAKPOINT;
  const mobileVisible = width <= MOBILE_BREAKPOINT;
  const isEnabled = (field: string) => {
    return !disabledFields.includes(field);
  };

  return (
    <thead>
      <tr>
        <th scope="col">
          <SortIndicatorComponent
            field="eaEventClassAndSort"
            name="Class"
            sort={sort}
            changeSort={changeSort}
            disabledFields={disabledFields}
          />
        </th>
        <th scope="col">
          <SortIndicatorComponent
            field="sectionName"
            name="Section"
            sort={sort}
            changeSort={changeSort}
            disabledFields={disabledFields}
          />
        </th>
        <th scope="col" colSpan={3}>
          <SortIndicatorComponent
            field="bridleNumber"
            name="Rider/BN"
            sort={sort}
            changeSort={changeSort}
            disabledFields={disabledFields}
          />
        </th>
        {desktopVisible &&
          [...Array(sjPhasesCount)].map((_, index) => (
            <React.Fragment key={index}>
              <th scope="col">
                <SortIndicatorComponent
                  field={`phase${index + 1}JF`}
                  name={`Phase ${index + 1} JF`}
                  sort={sort}
                  changeSort={changeSort}
                  disabledFields={disabledFields}
                />
              </th>
              <th scope="col" className="tablet-hidden">
                <SortIndicatorComponent
                  field={`phase${index + 1}TF`}
                  name={`Phase ${index + 1} TF`}
                  sort={sort}
                  changeSort={changeSort}
                  disabledFields={disabledFields}
                />
              </th>
              <th scope="col" className="tablet-hidden">
                <SortIndicatorComponent
                  field={`phase${index + 1}F`}
                  name={`Phase ${index + 1} F`}
                  sort={sort}
                  changeSort={changeSort}
                  disabledFields={disabledFields}
                />
              </th>
              <th scope="col" className="tablet-hidden">
                <SortIndicatorComponent
                  field={`phase${index + 1}RecTime`}
                  name={`Phase ${index + 1} Rec Time`}
                  sort={sort}
                  changeSort={changeSort}
                  disabledFields={disabledFields}
                />
              </th>
              <th scope="col" className="tablet-visible">
                <SortIndicatorComponent
                  field={`phase${index + 1}RecTime`}
                  name={`Phase ${index + 1} Rec Time`}
                  sort={sort}
                  changeSort={changeSort}
                  disabledFields={disabledFields}
                />
              </th>
            </React.Fragment>
          ))}
        {tabletVisible &&
          [...Array(sjPhasesCount)].map((_, index) => {
            const showTabletHeader =
              isEnabled(`phase${index + 1}JF`) ||
              isEnabled(`phase${index + 1}TF`) ||
              isEnabled(`phase${index + 1}F`) ||
              isEnabled(`phase${index + 1}RecTime`);
            return (
              <th scope="col" key={index}>
                <SortIndicatorComponent
                  field={''}
                  name={showTabletHeader ? `${index + 1} JF-TF-F-Time` : ''}
                  sort={sort}
                  changeSort={changeSort}
                  disabledFields={disabledFields}
                  tooltip="JF = Jumping Faults, TF = Time Faults, F = Total Faults, Time = Rec Time"
                />
              </th>
            );
          })}
        {mobileVisible && <th />}
        <th scope="col" className="mobile-hidden">
          <SortIndicatorComponent
            field="totalJFaults"
            name={tabletVisible ? 'F' : 'Total Faults'}
            sort={sort}
            changeSort={changeSort}
            disabledFields={disabledFields}
          />
        </th>
        <th scope="col">
          <SortIndicatorComponent
            field="sectionPlace"
            name="SP"
            sort={sort}
            changeSort={changeSort}
            disabledFields={disabledFields}
          />
        </th>
        <th scope="col">
          <SortIndicatorComponent
            field="sjPlace"
            name="OP"
            sort={sort}
            changeSort={changeSort}
            disabledFields={disabledFields}
          />
        </th>
        <th scope="col">
          <SortIndicatorComponent
            field="riderClassStatusName"
            name="Status"
            sort={sort}
            changeSort={changeSort}
            disabledFields={disabledFields}
          />
        </th>
      </tr>
    </thead>
  );
};

export default memo(ResultsSJTableHead);
